import { withErrorBoundary } from 'app/components/error-boundary';
import { Box, Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { images } from 'app/design/images';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { CredentialsFormFields, useOnboardingNavigation } from 'app/screens/onboarding/credentials.shared';
import { useCredentialsActivationFlags } from 'app/screens/onboarding/hooks/use-credentials-activation-flags';
import { useCredentialsForm } from 'app/screens/onboarding/hooks/use-credentials-form';
import { useCredentialsSignupFlow } from 'app/screens/onboarding/hooks/use-credentials-signup-flow';
import {
  ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
  onboardingLargeModalHeight,
} from 'app/screens/onboarding/onboarding-large-modal-layout';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect, useRef } from 'react';
import type { TextInput } from 'react-native';
import { KeyboardAvoidingView, Platform, useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();
const CONTENT_CONTAINER_STYLE = { paddingBottom: theme.spacing['spacing.general.x2'] };

const CredentialsScreenComponent: NavigationScreen<'credentials'> = ({ navigation }) => {
  const { current, max } = useOnboarding();
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { t: tOnboarding } = useTranslation('onboarding');

  const setPageTitle = usePageTitle('credentials');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { possibleActivationCode, requireActivationCode, userType } = useCredentialsActivationFlags();
  const { control, formState, getValues, handleSubmit } = useCredentialsForm(requireActivationCode);
  const signupFlow = useCredentialsSignupFlow({
    getValues,
    handleSubmit,
    requireActivationCode,
  });

  const { progressActiveColor } = useOnboardingNavigation(userType, { headerPresentation: 'overlay' });
  const { height: windowHeight } = useWindowDimensions();
  const modalHeight = onboardingLargeModalHeight(windowHeight);
  const insets = useSafeAreaInsets();

  useNavigationOptions(navigation, { headerBackVisible: false });

  const refPassword = useRef<TextInput>(null);
  const refPassword2 = useRef<TextInput>(null);
  const refInviteCode = useRef<TextInput>(null);

  const primaryProps = {
    disabled: !formState.isValid && !signupFlow.isLoggedIn,
    loading: signupFlow.isLoading,
    onPress: () => {
      if (signupFlow.isLoggedIn) {
        signupFlow.onSubmitInviteCode();
      } else {
        void signupFlow.onSubmit();
      }
    },
  };

  const formFieldsProps = {
    control,
    inviteCodeFirst: signupFlow.inviteCodeFirst,
    isLoading: signupFlow.isLoading,
    isLoggedIn: signupFlow.isLoggedIn,
    possibleActivationCode,
    refInviteCode,
    refPassword,
    refPassword2,
    requireActivationCode,
    t,
  };

  const errorsBlock = (
    <>
      {signupFlow.error ? (
        <RemoteError {...{ error: signupFlow.error, message: tAPI(signupFlow.error.message) }} />
      ) : null}
      {signupFlow.createOnePlanError ? (
        <RemoteError
          {...{ error: signupFlow.createOnePlanError, message: tAPI(signupFlow.createOnePlanError.message) }}
        />
      ) : null}
    </>
  );

  return (
    <Column backgroundColor="text.primary" flex={1}>
      <Box bottom={0} left={0} position="absolute" right={0} top={0}>
        <PCImage cachePolicy="memory-disk" contentFit="cover" flex={1} source={images.web.credentials} />
      </Box>
      <Column backgroundColor="text.primary" bottom={0} left={0} opacity={0.42} position="absolute" right={0} top={0} />
      <SafeAreaView edges={['top', 'left', 'right']} style={{ flex: 1 }}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              paddingTop: ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
            }}
          >
            <Column
              alignSelf="center"
              backgroundColor="surface.default"
              borderRadius="radius.xl"
              elevation={6}
              gap="spacing.general.x4"
              maxWidth={480}
              paddingHorizontal="spacing.general.x8"
              paddingTop="spacing.general.x8"
              shadowColor="black"
              shadowOffset={{ height: 4, width: 0 }}
              shadowOpacity={0.12}
              shadowRadius={16}
              style={{ height: modalHeight }}
              width="100%"
            >
              <ScrollView
                contentContainerStyle={CONTENT_CONTAINER_STYLE}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                style={{ flex: 1 }}
              >
                <ProgressIndicator activeColor={progressActiveColor} index={current} length={max} />

                <Text
                  fontWeight="600"
                  marginBottom="spacing.general.x4"
                  marginTop="spacing.general.x8"
                  textAlign="center"
                  variant="headerXSmall"
                >
                  {t('title')}
                </Text>

                <Column gap="spacing.general.x5">
                  <CredentialsFormFields {...formFieldsProps} />
                  {errorsBlock}
                </Column>
              </ScrollView>

              <Column style={{ paddingBottom: Math.max(insets.bottom, theme.spacing['spacing.general.x4']) }}>
                <Button variant="primary" {...primaryProps}>
                  <Text color="surface.default" textAlign="center">
                    {current === max - 1 ? tOnboarding('confirm') : tOnboarding('nextStep')}
                  </Text>
                </Button>
              </Column>
            </Column>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Column>
  );
};

export const CredentialsScreen: NavigationScreen<'credentials'> = withErrorBoundary(CredentialsScreenComponent);
